import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Modal } from "./intel/IntelKit";
import HorizonInput from "./HorizonInput";
import { api } from "../lib/api";

/**
 * Target Editor (P0 Company Trajectory architecture redesign, 2026-08).
 *
 * The ONE way a founder sets/edits the canonical Strategic Target directly
 * — no plan generation required. Writes straight to
 * `POST /api/intelligence/strategic-target` (the existing canonical
 * store). Horizon is a free positive number (years or months, normalized
 * to `horizon_years`) — never a fixed 1/3/5/10yr choice.
 */
export default function TargetEditorModal({ open, onClose, currentTarget, onSaved }) {
  const [targetCr, setTargetCr] = useState("");
  const [horizonYears, setHorizonYears] = useState(3);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setTargetCr(currentTarget?.target_value != null ? String(currentTarget.target_value) : "");
      setHorizonYears(currentTarget?.horizon_years != null ? Number(currentTarget.horizon_years) : 3);
      setError(null);
    }
  }, [open, currentTarget]);

  const save = async () => {
    const v = Number(targetCr);
    if (!v || v <= 0) { setError("Enter a target revenue greater than 0."); return; }
    if (!horizonYears || horizonYears <= 0) { setError("Enter a valid horizon."); return; }
    setSaving(true);
    setError(null);
    const res = await api.saveStrategicTarget({ targetValueCr: v, horizonYears });
    setSaving(false);
    if (res.ok && res.data?.saved) {
      onSaved?.(res.data.target);
      onClose();
    } else {
      setError(res.data?.message || res.error || "Could not save target. Please try again.");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit Strategic Target" testid="target-editor-modal">
      <p className="text-[12.5px] text-slate-500 mb-4">
        This is the ONE destination — C-ORE builds your trajectory, gap analysis and every
        executive's context around it. You decide what and by when; C-ORE tells you how realistic it is.
      </p>
      <div className="space-y-4">
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Target Revenue
          </label>
          <div className="mt-1 flex items-center rounded-md border border-slate-200 bg-white">
            <span className="px-2 text-[13px] text-slate-500">₹</span>
            <input
              type="number"
              step="0.1"
              min="0.01"
              value={targetCr}
              placeholder="42"
              onChange={(e) => setTargetCr(e.target.value)}
              data-testid="target-editor-revenue-input"
              className="w-full bg-transparent px-2 py-2 text-[14px] tabular-nums focus:outline-none"
            />
            <span className="pr-2 text-[12px] text-slate-500">Cr</span>
          </div>
        </div>
        <div>
          <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">
            Target Horizon
          </label>
          <div className="mt-1">
            <HorizonInput horizonYears={horizonYears} onChange={setHorizonYears} testIdPrefix="target-editor-horizon" />
          </div>
        </div>
        {error && (
          <div data-testid="target-editor-error" className="text-[12px] font-medium text-rose-600">{error}</div>
        )}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button" onClick={onClose} disabled={saving}
            data-testid="target-editor-cancel"
            className="rounded-lg border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="target-editor-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save Target
          </button>
        </div>
      </div>
    </Modal>
  );
}
